import dayjs, { type Dayjs } from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import { computed, ref, toValue, type ComputedRef, type MaybeRefOrGetter, type Ref } from 'vue'
// types
import type { DatePickerDay, DatePickerView } from '@/types/components/dateTime'

dayjs.extend(isoWeek)

/** Any value the calendar can normalize into a Dayjs */
type DateInput = string | Date | Dayjs | null | undefined

/** Options accepted by useDatePickerCalendar; all constraints are reactive-friendly */
export interface UseDatePickerCalendarOptions {
  /** Month initially displayed (defaults to the current month) */
  initialMonth?: Dayjs
  /** Days before this date are disabled */
  minDate?: MaybeRefOrGetter<DateInput>
  /** Days after this date are disabled */
  maxDate?: MaybeRefOrGetter<DateInput>
  /** Specific days that are disabled */
  excludeDates?: MaybeRefOrGetter<Array<string | Date | Dayjs> | undefined>
  /** Maximum number of days in a range (inclusive), enforced while completing a range */
  maxRange?: MaybeRefOrGetter<number | null | undefined>
  /** Pending range start, used together with maxRange to disable days too far away */
  rangeStart?: MaybeRefOrGetter<Dayjs | null | undefined>
}

/** Everything the DatePickerCalendar component needs to render and navigate a single month */
export interface UseDatePickerCalendarReturn {
  /** First day of the month currently displayed */
  viewMonth: Ref<Dayjs>
  /** Active in-place view: date grid, month grid, or year grid */
  view: Ref<DatePickerView>
  /** 6 rows x 7 days grid for the displayed month */
  weeks: ComputedRef<DatePickerDay[][]>
  /** Two-letter weekday headers, Monday first */
  weekdays: string[]
  /** Short month names for the month grid */
  monthNames: string[]
  /** 12-year page shown in the year grid */
  years: ComputedRef<number[]>
  /** Title for the year grid header, e.g. '2016 - 2027' */
  yearGroupTitle: ComputedRef<string>
  /** View-aware forward navigation (month / year / year-group) */
  next: () => void
  /** View-aware backward navigation (month / year / year-group) */
  prev: () => void
  /** Jump the displayed month to the month containing the given date */
  goToDate: (date: Dayjs) => void
  /** Swap the date grid for the in-place month picker */
  openMonthView: () => void
  /** Swap the current view for the in-place year picker */
  openYearView: () => void
  /** Pick a month (0-11) from the month grid and return to the date grid */
  selectMonth: (monthIndex: number) => void
  /** Pick a year from the year grid and move on to the month grid */
  selectYear: (year: number) => void
  /** Whether a given day violates min/max/excluded/maxRange constraints */
  isDayDisabled: (date: Dayjs) => boolean
}

/** Number of years shown per page in the year grid */
const YEARS_PER_PAGE = 12

/**
 * Normalizes any supported date input to a Dayjs instance (lenient parse), or null.
 */
function toDayjs(value: DateInput): Dayjs | null {
  // Nothing to parse
  if (value === null || value === undefined || value === '') return null
  const parsed = dayjs(value)
  return parsed.isValid() ? parsed : null
}

/**
 * Composable holding all calendar grid state and logic: single-month week grid
 * generation, disabled-day rules, month/year navigation, and the in-place
 * month/year picker views with 12-year pagination.
 */
export function useDatePickerCalendar(
  options: UseDatePickerCalendarOptions = {}
): UseDatePickerCalendarReturn {
  const viewMonth = ref<Dayjs>((options.initialMonth ?? dayjs()).startOf('month'))
  const view = ref<DatePickerView>('date')

  // Base year (multiple of 12) for the current year-grid page
  const yearGroupBase = ref(Math.floor(viewMonth.value.year() / YEARS_PER_PAGE) * YEARS_PER_PAGE)

  const weekdays = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

  /**
   * Whether a given day violates min/max/excluded/maxRange constraints.
   */
  function isDayDisabled(date: Dayjs): boolean {
    const min = toDayjs(toValue(options.minDate))
    const max = toDayjs(toValue(options.maxDate))

    // Outside the allowed min/max window
    if (min && date.isBefore(min, 'day')) return true
    if (max && date.isAfter(max, 'day')) return true

    // Explicitly excluded days
    const excluded = toValue(options.excludeDates) ?? []
    if (excluded.some(excludedDate => date.isSame(toDayjs(excludedDate), 'day'))) return true

    // Too far from the pending range start
    const maxRange = toValue(options.maxRange)
    const rangeStart = toValue(options.rangeStart)
    if (maxRange && rangeStart) {
      const rangeLength = Math.abs(date.diff(rangeStart, 'day')) + 1
      if (rangeLength > maxRange) return true
    }

    return false
  }

  const weeks = computed<DatePickerDay[][]>(() => {
    const rows: DatePickerDay[][] = []
    // Start at the Monday of the week containing the 1st of the month
    let current = viewMonth.value.startOf('month').startOf('isoWeek')

    // Always render 6 rows so the grid height is stable
    for (let weekIndex = 0; weekIndex < 6; weekIndex++) {
      const week: DatePickerDay[] = []
      for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
        week.push({
          day: current.date(),
          date: current,
          isCurrentMonth: current.month() === viewMonth.value.month(),
          isDisabled: isDayDisabled(current)
        })
        current = current.add(1, 'day')
      }
      rows.push(week)
    }
    return rows
  })

  const years = computed<number[]>(() =>
    Array.from({ length: YEARS_PER_PAGE }, (_, index) => yearGroupBase.value + index)
  )

  const yearGroupTitle = computed(() => `${yearGroupBase.value} - ${yearGroupBase.value + YEARS_PER_PAGE - 1}`)

  /**
   * View-aware forward navigation: next month, next year, or next year page.
   */
  function next(): void {
    if (view.value === 'date') {
      viewMonth.value = viewMonth.value.add(1, 'month')
    } else if (view.value === 'month') {
      viewMonth.value = viewMonth.value.add(1, 'year')
    } else {
      yearGroupBase.value += YEARS_PER_PAGE
    }
  }

  /**
   * View-aware backward navigation: previous month, previous year, or previous year page.
   */
  function prev(): void {
    if (view.value === 'date') {
      viewMonth.value = viewMonth.value.subtract(1, 'month')
    } else if (view.value === 'month') {
      viewMonth.value = viewMonth.value.subtract(1, 'year')
    } else {
      yearGroupBase.value -= YEARS_PER_PAGE
    }
  }

  /**
   * Jumps the displayed month to the month containing the given date.
   */
  function goToDate(date: Dayjs): void {
    viewMonth.value = date.startOf('month')
    yearGroupBase.value = Math.floor(date.year() / YEARS_PER_PAGE) * YEARS_PER_PAGE
  }

  /**
   * Swaps the date grid for the in-place month picker.
   */
  function openMonthView(): void {
    view.value = 'month'
  }

  /**
   * Swaps the current view for the in-place year picker, paged to the displayed year.
   */
  function openYearView(): void {
    yearGroupBase.value = Math.floor(viewMonth.value.year() / YEARS_PER_PAGE) * YEARS_PER_PAGE
    view.value = 'year'
  }

  /**
   * Picks a month (0-11) from the month grid and returns to the date grid.
   */
  function selectMonth(monthIndex: number): void {
    viewMonth.value = viewMonth.value.month(monthIndex).startOf('month')
    view.value = 'date'
  }

  /**
   * Picks a year from the year grid and moves on to the month grid.
   */
  function selectYear(year: number): void {
    viewMonth.value = viewMonth.value.year(year).startOf('month')
    view.value = 'month'
  }

  return {
    viewMonth,
    view,
    weeks,
    weekdays,
    monthNames,
    years,
    yearGroupTitle,
    next,
    prev,
    goToDate,
    openMonthView,
    openYearView,
    selectMonth,
    selectYear,
    isDayDisabled
  }
}
